import React, { useEffect, useRef } from 'react';
import { AutoComplete } from 'primereact/autocomplete';
import { ReactComponent as ArrowIcon } from '../../../assets/shared/ic-shared-down-arrow.svg';
import { ReactComponent as ClearIcon } from '../../../assets/shared/ic-shared-clear.svg';
import { useAutoCompleteLogic } from './hooks/useAutoCompleteLogic';
import { ErrorMessage } from './components/ErrorMessage';
import { DefaultItemTemplate } from './components/DefaultItemTemplate';
import { twMerge } from 'tailwind-merge';
import { useFontClass } from '../../tokens';

export interface ListItem {
  title: string;
  value: string;
  template?: React.ReactNode;
  persist?: boolean;
}

interface LiliAutoCompleteProps {
  isForceSelection?: boolean;
  onItemSelection: (item: ListItem) => void;
  selectedItem: ListItem | null;
  autoCompleteItems: ListItem[];
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string;
  noResultsMessage?: string;
  inputId?: string;
  shouldScrollOnOpen?: boolean;
  tabIndex?: number;
  onHide?: () => void;
  autoComplete?: string;
  onBlur?: () => void;
  onClear?: () => void;
}

export const LiliAutoComplete = ({
  onItemSelection,
  noResultsMessage,
  errorMessage,
  selectedItem,
  placeholder,
  disabled,
  autoCompleteItems,
  isForceSelection = true,
  inputId,
  shouldScrollOnOpen,
  tabIndex,
  onHide,
  autoComplete = 'new-password',
  onBlur,
  onClear,
}: LiliAutoCompleteProps) => {
  const {
    componentRef,
    isPanelVisible,
    filterInputValue,
    suggestedItems,
    highlightedIndex,
    highlightedItemRef,
    setSuggestionsOnChange,
    setFilterInputValue,
    setIsPanelVisible,
    setHighlightedIndex,
    handleKeyDown,
  } = useAutoCompleteLogic(autoCompleteItems, selectedItem);
  const { fontClass, placeholderFontClass } = useFontClass();

  const arrowStyles = twMerge(isPanelVisible ? 'rotate-180' : '');

  const elWrapper = useRef<HTMLDivElement>(null);

  const elAutoComplete = useRef<any>(null);

  const openAutoComplete = () => {
    elAutoComplete.current?.show();
  };

  useEffect(() => {
    if (isPanelVisible && shouldScrollOnOpen) {
      elWrapper.current?.scrollIntoView({
        block: 'start',
        inline: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [isPanelVisible]);

  // Add keyboard event listener
  useEffect(() => {
    const handleKeyDownEvent = (e: KeyboardEvent) => {
      handleKeyDown(e, onItemSelection);
    };

    if (isPanelVisible) {
      document.addEventListener('keydown', handleKeyDownEvent);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDownEvent);
    };
  }, [isPanelVisible, handleKeyDown, onItemSelection]);

  return (
    <div ref={elWrapper} id='lili-auto-complete' className='flex flex-col w-full'>
      <div ref={componentRef} className='w-full relative'>
        <AutoComplete
          inputId={inputId}
          tabIndex={tabIndex}
          unstyled
          autoComplete={autoComplete}
          disabled={disabled}
          field='title'
          ref={elAutoComplete}
          onClick={openAutoComplete}
          pt={{
            // This is a bug in PrimeReact, the emptyMesage prop is mispelled in the dom but correctly in the typing, causing a mismatch.
            // Might be solved in later version, check again after updating.
            // @ts-expect-error
            emptyMesage: {
              className: 'px-4 py-3 text-text-main-tertiary',
            },
          }}
          onFocus={openAutoComplete}
          data-hj-allow
          placeholder={placeholder || 'Select'}
          inputClassName={twMerge(
            `pl-[16px] w-full hover:bg-surface-primary-bw-01 pr-[40px] py-[14px] rounded-[8px] border border-outline-primary-bw-04 focus:border-outline-brand-primary outline-none text-[16px] truncate ${placeholderFontClass} placeholder:text-text-main-tertiary text-text-main-primary shadow-none ${fontClass}`,
            onClear && 'pr-[60px]',
            errorMessage && '!border-outline-negative-primary placeholder:!text-text-negative-red'
          )}
          className='w-full'
          panelClassName='mt-[5px] bg-surface-primary-bw-00 md:rounded-[8px] border border-outline-primary-bw-03 shadow-lg overflow-auto z-[9999]'
          itemTemplate={(item: ListItem) => {
            const isSelected = item.value === selectedItem?.value;
            const isHighlighted =
              suggestedItems.findIndex((suggItem) => suggItem.value === item.value) ===
              highlightedIndex;
            return item?.template ? (
              item.template
            ) : (
              <DefaultItemTemplate
                item={item}
                isSelected={isSelected}
                isHighlighted={isHighlighted}
                ref={isHighlighted ? highlightedItemRef : null}
                onItemSelection={() => {
                  onItemSelection(item);
                  setHighlightedIndex(-1);
                  setTimeout(() => {
                    elAutoComplete.current?.hide();
                  }, 5);
                }}
              />
            );
          }}
          loadingIcon={''}
          onBlur={() => {
            onBlur?.();
            setFilterInputValue(selectedItem?.title || '');
          }}
          forceSelection={isForceSelection}
          value={filterInputValue}
          suggestions={suggestedItems}
          completeMethod={setSuggestionsOnChange}
          onChange={(e) =>
            setFilterInputValue(typeof e.value === 'string' ? e.value : (e.value?.title ?? ''))
          }
          emptyMessage={noResultsMessage}
          showEmptyMessage={!!noResultsMessage}
          onShow={() => setIsPanelVisible(true)}
          onHide={() => {
            setIsPanelVisible(false);
            if (onHide) {
              onHide();
            }
          }}
        />
        <div className='absolute right-[12px] top-[50%] transform -translate-y-1/2 flex items-center gap-[6px]'>
          {onClear && filterInputValue && !disabled && (
            <button
              type='button'
              onClick={() => {
                setFilterInputValue('');
                onClear();
              }}>
              <ClearIcon className='text-text-main-tertiary w-[20px]' />
            </button>
          )}
          <ArrowIcon
            className={`pointer-events-none transition-transform duration-300 text-outline-primary-bw-06 ${arrowStyles}`}
          />
        </div>
      </div>
      {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
    </div>
  );
};
